import React, {useCallback, useState, useEffect, useContext, useMemo} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExternalLink} from "@fortawesome/free-solid-svg-icons";

// Hooks
import useApi from "../../hooks/useApi.js";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";

// Utils
import {prettyDateInUserTimezone} from "../../utils/helpers.js";
import {formatWeatherFieldValue, isReviewWeatherField} from "./reviewConditions.js";

// Components
import FilePreview from "../../components/FilePreview.js";

// Styles
import {pad, status as statusColors} from "../../style/components/variables.js";
import {Abbr, Button, Inline, Label, Pill, Section, Text} from "../../style/components/general.js";

const Review = ({
  checksheet,
  review: {id, title, description, limit, fields},
  setRecord,
  date,
  loading,
  setLoading
}) => {
  const {api: apiRecords} = useApi("checksheet-records");
  const {api: apiFiles} = useApi("files");

  const {settings} = useContext(SettingsContext);

  const [records, setRecords] = useState();

  const weatherFields = useMemo(
    () => (fields || []).filter(({value}) => isReviewWeatherField(value)),
    [fields]
  );
  const responseFields = useMemo(
    () => (fields || []).filter(({value}) => !isReviewWeatherField(value)),
    [fields]
  );

  const getStateName = record => {
    if (record.state)
      return record.state.state.name ? record.state.state.name.toUpperCase() : "STATE";
    return "DEFAULT";
  };

  const handleFiles = useCallback(
    async fileId => {
      const res = await apiFiles.callGet(fileId);
      if (res.status === 200 && res.data)
        return <FilePreview selected={res.data} download={false} />;
      return "File not found.";
    },
    [apiFiles]
  );

  const formatRecords = useCallback(
    async data => {
      const formatted = {};
      await Promise.all(
        (data.records || []).map(
          async ({
            id: recordId,
            completedAt,
            dateDue,
            draft,
            submission,
            builder,
            state,
            weather,
            precipitationChance
          }) => {
            const key = completedAt || dateDue;
            formatted[key] = {
              id: recordId,
              completedAt,
              draft,
              dateDue,
              state,
              weather,
              precipitationChance,
              values: []
            };
            const responses = submission?.responses || {};

            await Promise.all(
              (responseFields || []).map(async ({name, value}) => {
                let formattedValue = responses[value];

                if (formattedValue === "_CONDITION_UNSATISFIED")
                  formattedValue = "Field was hidden on this submission by a condition.";

                if (builder?.byId?.[value]?.type === "upload" && formattedValue)
                  formattedValue = await handleFiles(responses[value]);

                if (value in responses && formattedValue)
                  formatted[key].values.push({
                    key: name,
                    value: formattedValue
                  });
              })
            );
          }
        )
      );

      setRecords(formatted);
      setLoading(prev => ({...prev, [id]: false}));
    },
    [responseFields, handleFiles, setLoading, id]
  );

  useEffect(() => {
    if (!records)
      apiRecords
        .callGet(null, {
          facilityChecksheetId: checksheet.id,
          limit,
          end: date,
          fields: responseFields?.map(({value}) => value)?.toString(),
          withResponses: true,
          withBuilder: true,
          withWeather: true
        })
        .then(({status, data}) => {
          if (status === 200 && data?.records?.length > 0) formatRecords(data);
          else if (status === 200) setLoading(prev => ({...prev, [id]: false}));
        });
  }, [records, apiRecords, checksheet, limit, formatRecords, date, responseFields, setLoading, id]);

  const renderValues = recordKey => {
    const record = records[recordKey];
    // Weather attrs from each past record's serialize(with_weather) payload.
    const weatherEntry = record.weather
      ? {
          ...record.weather,
          precipitationChance: record.precipitationChance ?? record.weather.precipitationChance
        }
      : null;
    const weatherPairs = weatherFields.map(({name, value}) => ({
      key: name,
      value: formatWeatherFieldValue(weatherEntry, value) || "No weather data"
    }));
    return [...weatherPairs, ...(record.values || [])];
  };

  return (
    <Section key={title}>
      <Description>{description}</Description>
      {!loading &&
        records &&
        Object.keys(records)?.map(d => {
          const record = records[d];
          return (
            <div key={record.id}>
              <Inline>
                <Text>
                  <Label bold>Record:</Label>{" "}
                  {prettyDateInUserTimezone(d, settings?.timezone, "MMM D, YYYY")}
                </Text>
                {record.completedAt && checksheet?.state && (
                  <Pill color={record.state?.color ? `#${record.state.color}` : undefined}>
                    {getStateName(record)}
                  </Pill>
                )}
                {!record.completedAt && (
                  <Pill color={record.draft ? statusColors.warning : statusColors.error}>
                    {record.draft ? "Incomplete" : "Overdue"}
                  </Pill>
                )}
                {setRecord && (
                  <Button
                    type="button"
                    onClick={() =>
                      apiRecords.callGet(record.id).then(({status, data}) => {
                        if (status === 200 && data) setRecord(data);
                      })
                    }
                    quiet>
                    <Abbr title="View Full Record">
                      <FontAwesomeIcon icon={faExternalLink} />
                    </Abbr>
                  </Button>
                )}
              </Inline>
              {renderValues(d).map(pair => (
                <Row key={pair.key}>
                  <Label bold>{pair.key}:</Label> {pair.value || "NO RESPONSE PROVIDED."}
                </Row>
              ))}
            </div>
          );
        })}
      {(fields?.length > 0 || weatherFields.length > 0) && !records && !loading && (
        <Text>No past records found for selected fields</Text>
      )}
    </Section>
  );
};

Review.propTypes = {
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired,
  review: PropTypes.objectOf(PropTypes.any).isRequired,
  setRecord: PropTypes.func,
  date: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired
};

// Style Overrides
const Description = styled(Text)`
  margin: ${pad}px 0;
`;

const Row = styled.div`
  margin-bottom: ${pad}px;
`;

export default Review;
